import { WSEmptyState, WSList } from "@wingspanhq/fe-component-library";
import { ListNotificationsFilterParameterStatusAnyOfInner as NotificationStatus } from "@wingspanhq/wingspan-api-sdk";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useSession } from "@hooks/useSession";
import { useMutationViewNotification } from "../../../../query/notifications/mutations/useMutationViewNotification";
import { openInNewTab } from "../../../../shared/utils/openInNewTab";
import { useTrackClickNotification } from "../../utils/useTrackClickNotification";
import {
  getNotificationTemplate,
  NotificationTemplateParameters,
  WSNotificationResponse
} from "../Notifications";
import { TodoItem } from "../TodoItem";
import { selectorGroupedTodos } from "../../selectors/selectorGroupedTodos";
import { selectorPayerLogoFileIdFromContext } from "../../selectors/selectorPayerLogoFileIdFromContext";
import { selectorPayerNameFromContext } from "../../selectors/selectorPayerNameFromContext";

type Props = {
  notifications: WSNotificationResponse[];
};

export const TodoList: React.FC<Props> = props => {
  const { personId } = useSession();
  const history = useHistory();

  const todoGroups = useMemo(
    () => selectorGroupedTodos(props.notifications),
    [props.notifications]
  );

  const trackClickNotification = useTrackClickNotification(personId);
  const [readNotification] = useMutationViewNotification();

  return (
    <WSList>
      {todoGroups.length === 0 ? (
        <WSEmptyState
          type="tasks"
          title="You're all set for now"
          description={
            <>
              New tasks will appear here as they arise.
              <br />
              Have a great day!.
            </>
          }
        />
      ) : (
        todoGroups.map(({ group, ...notification }) => {
          const taskPreset = getNotificationTemplate(notification.handle);

          const Button = taskPreset?.Footer;
          const Header = taskPreset?.Header;
          const taskProps = {
            ...notification,
            group
          } as NotificationTemplateParameters;
          const hasGroups = group.length > 1;

          const logoFileId = selectorPayerLogoFileIdFromContext(
            notification.context
          );
          const companyName = selectorPayerNameFromContext(
            notification.context
          );

          return (
            <TodoItem
              key={notification.id}
              isPending={taskPreset?.isPending}
              onClick={() => {
                if (group.length === 1) {
                  trackClickNotification(notification);
                }
              }}
              viewed={group.every(
                data => data.status === NotificationStatus.VIEWED
              )}
              body={
                taskPreset
                  ? taskPreset.renderBody(taskProps, history)
                  : notification.message
              }
              buttonAction={
                taskPreset
                  ? taskPreset.buttonAction?.(taskProps, history)
                  : notification.actionUrl
                    ? () => {
                        const { hostname, pathname, search, hash } = new URL(
                          notification.actionUrl!
                        );

                        if (hostname !== window.location.hostname) {
                          openInNewTab(notification.actionUrl!);
                          return;
                        }

                        history.push(pathname + search + hash);
                      }
                    : undefined
              }
              buttonActionAsync={
                taskPreset
                  ? taskPreset.buttonActionAsync?.(taskProps, history)
                  : undefined
              }
              title={
                taskPreset
                  ? taskPreset.renderTitle(taskProps)
                  : notification.subject
              }
              footer={Button ? <Button {...taskProps} /> : null}
              header={Header ? <Header {...taskProps} /> : null}
              buttonText={
                taskPreset
                  ? taskPreset.buttonText?.(taskProps)
                  : (notification.actionTitle ?? undefined)
              }
              numberOfTasks={hasGroups ? group.length : undefined}
              company={
                logoFileId && companyName && !hasGroups
                  ? {
                      logoFileId,
                      name: companyName
                    }
                  : undefined
              }
              onRead={
                group.some(data => data.status !== NotificationStatus.VIEWED)
                  ? () => {
                      group.forEach(data => {
                        if (data.status !== NotificationStatus.VIEWED) {
                          readNotification(data.id);
                        }
                      });
                    }
                  : undefined
              }
            />
          );
        })
      )}
    </WSList>
  );
};
